import {
  useCreateBeneficiary,
  useDeleteBeneficiary
} from 'common/queries-and-mutations/beneficiary';
import { useGetChildrenList } from 'common/queries-and-mutations/children';
import { useGetSpousesList } from 'common/queries-and-mutations/spouse';
import { Icon, Icons } from 'components/Icon';
import SelectMultipleBeneficiaryModal, {
  SpouseChildDto
} from 'components/Modal/SelectMultipleBeneficiaryModal';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BeneficiaryTypeEnum } from 'apps/user/types/user.enum.type';
import { Loader } from 'components/Loader';
import { showRemoveButton } from 'utils';
import TextField from 'components/Form/TextField';
import GenderField from 'components/Form/GenderField';
import CustomSelect from 'components/Form/CustomSelect';
import { MaritalStatus } from 'constants/marital-status';
import { RelationshipType } from 'constants/bank-account-type';
import Spacer from 'components/Spacer';
import Button from 'components/Form/Button';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { services } from 'service';

const EnhancedSelectMultipleBeneficiaryModal = withCreatePortal(
  SelectMultipleBeneficiaryModal
);

export default function AddBeneficiary() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showSelectUser, setShowSelectUser] = useState(false);

  const { data: childrens } = useGetChildrenList();

  const { data: spouses } = useGetSpousesList();
  const { mutate, isSuccess, isLoading, isError } = useCreateBeneficiary();
  const { mutate: deleteBeneficiary, isLoading: isDeleting } =
    useDeleteBeneficiary();

  const shouldShowSelectField =
    (spouses && spouses?.length > 0) || (childrens && childrens?.length > 0);

  const formik = useFormik({
    initialValues: {
      beneficiaries: [
        {
          beneficiaryId: null,
          beneficiaryFirstName: '',
          beneficiaryMiddleName: '',
          beneficiaryLastName: '',
          beneficiaryType: '',
          relationship: '',
          address: '',
          emailAddress: '',
          dateOfBirth: '',
          maritalStatus: '',
          rcNumber: '',
          incorporationDate: '',
          incorporationPlace: '',
          gender: ''
        }
      ]
    },
    validationSchema: Yup.object({
      beneficiaries: Yup.array().of(
        Yup.object().shape({
          beneficiaryFirstName: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Beneficiary first name is required')
              .nullable(),
            otherwise: Yup.string().required('Name is required').nullable()
          }),
          beneficiaryLastName: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Beneficiary last name is required')
              .nullable(),
            otherwise: Yup.string()
          }),
          beneficiaryType: Yup.string()
            .required('Beneficiary type is required')
            .nullable(),
          rcNumber: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.CORPORATE,
            then: Yup.string().required('RC Number is required').nullable(),
            otherwise: Yup.string()
          }),
          dateOfBirth: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Date of Birth is required').nullable(),
            otherwise: Yup.string()
          }),
          relationship: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Relationship is required').nullable(),
            otherwise: Yup.string()
          }),
          address: Yup.string().required('Address is required').nullable(),
          gender: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Gender is required').nullable(),
            otherwise: Yup.string()
          }),
          maritalStatus: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Marital status is required')
              .nullable(),
            otherwise: Yup.string()
          }),
          emailAddress: Yup.string()
            .email('Invalid email')
            .required('Email is required')
            .nullable()
            .test('is-allowed', 'Email already in use', async function (value) {
              if (value) {
                const isInUse =
                  await services.beneficiaryService.checkIfEmailInUse(value);
                return !isInUse;
              }
              return true;
            })
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBeneficarySubmit(values);
    }
  });

  const addMoreBeneficary = () => {
    formik.values.beneficiaries.push({
      beneficiaryId: null,
      beneficiaryFirstName: '',
      beneficiaryMiddleName: '',
      beneficiaryLastName: '',
      beneficiaryType: '',
      relationship: '',
      address: '',
      emailAddress: '',
      dateOfBirth: '',
      maritalStatus: '',
      rcNumber: '',
      incorporationDate: '',
      incorporationPlace: '',
      gender: ''
    });
    formik.setFieldValue('beneficiaries', [...formik.values.beneficiaries]);
  };

  const removeBeneficary = (
    indexToRemove: number,
    beneficiaryId?: number | null
  ) => {
    if (beneficiaryId) {
      deleteBeneficiary(beneficiaryId);
    }
    formik.values.beneficiaries.splice(indexToRemove, 1);
    formik.setFieldValue('beneficiaries', [...formik.values.beneficiaries]);
  };

  const onBeneficarySubmit = (formValue: any) => {
    mutate(formValue);
  };

  const onBeneficiarySelect = (spouseChildDto: SpouseChildDto[]) => {
    const children = spouseChildDto.filter(
      item => item.relationship === 'Child'
    );
    let childBeneficiary: any[] = [];
    let spouseBeneficiary: any[] = [];
    if (children.length) {
      childBeneficiary = children.map(child => ({
        childrenId: parseInt((child.id.match(/\d+/) || ['0'])[0]),
        beneficiaryFirstName: child.firstName,
        beneficiaryMiddleName: child.middleName,
        beneficiaryLastName: child.lastName,
        beneficiaryType: BeneficiaryTypeEnum.INDIVIDUAL,
        relationship: child.relationship,
        address: child.address,
        gender: child.gender,
        emailAddress: '',
        dateOfBirth: child.dateOfBirth,
        maritalStatus: '',
        rcNumber: '',
        incorporationDate: '',
        incorporationPlace: ''
      }));
    }
    const spouses = spouseChildDto.filter(
      item => item.relationship === 'Spouse'
    );
    if (spouses.length) {
      spouseBeneficiary = spouses.map(spouse => ({
        spouseId: parseInt((spouse.id.match(/\d+/) || ['0'])[0]),
        beneficiaryFirstName: spouse.firstName,
        beneficiaryMiddleName: spouse.middleName,
        beneficiaryLastName: spouse.lastName,
        beneficiaryType: BeneficiaryTypeEnum.INDIVIDUAL,
        relationship: spouse.relationship,
        address: spouse.address,
        emailAddress: '',
        dateOfBirth: spouse.dateOfBirth,
        maritalStatus: '',
        rcNumber: '',
        incorporationDate: '',
        incorporationPlace: ''
      }));
    }
    formik.setFieldValue(`beneficiaries`, [
      ...formik.values.beneficiaries.filter(
        bene => bene.beneficiaryType !== ''
      ),
      ...spouseBeneficiary,
      ...childBeneficiary
    ]);

    setShowSelectUser(false);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert message="Beneficiary Successfully Added" type="success" />);
      setIsSubmitting(false);
      formik.resetForm();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  return (
    <>
      <div className="mb-10 grid grid-cols-3">
        <div className="col-span-2 col-start-1">
          <Breadcrumb title="Beneficiary Management" />
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="h-fit w-full rounded-md bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
          <div className="mb-7 flex flex-row items-center gap-x-6 px-6">
            <p className="text-xl font-bold text-[#353535]">Add Beneficiary</p>
            {shouldShowSelectField && (
              <div
                className="flex cursor-pointer items-center gap-1"
                role="button"
                onClick={() => setShowSelectUser(true)}
              >
                <Icon name={Icons.Add} width={15} height={15} fill="#FF6606" />
                <p className="font-circular text-base font-medium text-primary-800">
                  Select from Spouse & Children
                </p>
              </div>
            )}
          </div>
          {formik.values.beneficiaries.map((beneficiary, index) => (
            <div key={index} className="mb-8 px-6">
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`beneficiaries[${index}].beneficiaryFirstName`}
                    placeholder=""
                    value={beneficiary.beneficiaryFirstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={
                      beneficiary.beneficiaryType ===
                      BeneficiaryTypeEnum.INDIVIDUAL
                        ? 'Beneficiary First Name'
                        : 'Name'
                    }
                    error={
                      formik.touched.beneficiaries
                        ? formik.errors.beneficiaries?.[index] &&
                          formik.errors.beneficiaries.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.beneficiaries[index])
                            ).beneficiaryFirstName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <div className="relative mb-2 flex items-center justify-between">
                    <span className="flex items-center">
                      <label className="text-[12px] font-medium text-[#353535]">
                        Beneficiary Type
                      </label>
                      <div className="mx-2 mb-2">
                        <Spacer width={3} />
                        <Icon name={Icons.Required} />
                      </div>
                    </span>
                  </div>
                  <div className="mt-5 flex items-center gap-10">
                    <button
                      type="button"
                      onClick={() =>
                        formik.setFieldValue(
                          `beneficiaries[${index}].beneficiaryType`,
                          BeneficiaryTypeEnum.INDIVIDUAL
                        )
                      }
                      className="flex items-center gap-3"
                    >
                      <span className="text-base font-bold text-[#353535]">
                        Individual
                      </span>
                      <Icon
                        name={
                          beneficiary.beneficiaryType ===
                          BeneficiaryTypeEnum.INDIVIDUAL
                            ? Icons.CheckedBox
                            : Icons.UncheckBox
                        }
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formik.setFieldValue(
                          `beneficiaries[${index}].beneficiaryType`,
                          BeneficiaryTypeEnum.CORPORATE
                        )
                      }
                      className="flex items-center gap-3"
                    >
                      <span className="text-base font-bold text-[#353535]">
                        Corporate
                      </span>
                      <Icon
                        name={
                          beneficiary.beneficiaryType ===
                          BeneficiaryTypeEnum.CORPORATE
                            ? Icons.CheckedBox
                            : Icons.UncheckBox
                        }
                      />
                    </button>
                  </div>
                </div>
              </div>
              {beneficiary.beneficiaryType ===
                BeneficiaryTypeEnum.INDIVIDUAL && (
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`beneficiaries[${index}].beneficiaryMiddleName`}
                      placeholder=""
                      value={beneficiary.beneficiaryMiddleName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Beneficiary Middle Name"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).beneficiaryMiddleName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`beneficiaries[${index}].beneficiaryLastName`}
                      placeholder=""
                      value={beneficiary.beneficiaryLastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Beneficiary Last Name"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).beneficiaryLastName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
              )}
              {beneficiary.beneficiaryType ===
                BeneficiaryTypeEnum.INDIVIDUAL && (
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={RelationshipType}
                      selectedOption={{
                        value: beneficiary.relationship,
                        label: beneficiary.relationship
                      }}
                      handleOptionChange={option => {
                        formik.setFieldValue(
                          `beneficiaries[${index}].relationship`,
                          option?.value
                        );
                      }}
                      label="Relationship"
                      name="relationship"
                      isRequired
                      isSearchable
                      className="h-[3.5rem] rounded-2xl"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).relationship
                            : ''
                          : ''
                      }
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`beneficiaries[${index}].address`}
                      placeholder=""
                      isRequired
                      value={beneficiary.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Home Address"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).address
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
              )}
              {beneficiary.beneficiaryType ===
                BeneficiaryTypeEnum.CORPORATE && (
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`beneficiaries[${index}].rcNumber`}
                      placeholder=""
                      value={beneficiary.rcNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="RC Number"
                      isRequired
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).rcNumber
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`beneficiaries[${index}].address`}
                      placeholder=""
                      isRequired
                      value={beneficiary.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Company Address"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).address
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  {beneficiary.beneficiaryType && (
                    <TextField
                      name={`beneficiaries[${index}].emailAddress`}
                      placeholder=""
                      isRequired
                      value={beneficiary.emailAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Email Address"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).emailAddress
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  )}
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  {beneficiary.beneficiaryType ===
                    BeneficiaryTypeEnum.INDIVIDUAL && (
                    <TextField
                      name={`beneficiaries[${index}].dateOfBirth`}
                      type="date"
                      isRequired
                      placeholder=""
                      value={beneficiary.dateOfBirth}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Date of Birth"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).dateOfBirth
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                      validateDate={false}
                    />
                  )}
                  {beneficiary.beneficiaryType ===
                    BeneficiaryTypeEnum.CORPORATE && (
                    <TextField
                      name={`beneficiaries[${index}].incorporationDate`}
                      type="date"
                      placeholder=""
                      value={beneficiary.incorporationDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Date of Incorporation"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).incorporationDate
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  )}
                </div>
              </div>
              {beneficiary.beneficiaryType ===
                BeneficiaryTypeEnum.INDIVIDUAL && (
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={MaritalStatus}
                      selectedOption={{
                        value: beneficiary.maritalStatus,
                        label: beneficiary.maritalStatus
                      }}
                      handleOptionChange={option => {
                        formik.setFieldValue(
                          `beneficiaries[${index}].maritalStatus`,
                          option?.value
                        );
                      }}
                      label="Marital Status"
                      name="maritalStatus"
                      isSearchable
                      className="h-[3.5rem] rounded-2xl"
                      isRequired
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).maritalStatus
                            : ''
                          : ''
                      }
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <GenderField
                      gender={beneficiary.gender}
                      onSelect={gender =>
                        formik.setFieldValue(
                          `beneficiaries[${index}].gender`,
                          gender
                        )
                      }
                      isRequired
                    />
                  </div>
                </div>
              )}
              {beneficiary.beneficiaryType ===
                BeneficiaryTypeEnum.CORPORATE && (
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`beneficiaries[${index}].incorporationPlace`}
                      placeholder=""
                      value={beneficiary.incorporationPlace}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Place of Incorporation"
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).incorporationPlace
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
              )}
              {showRemoveButton(index, beneficiary.beneficiaryId, true) && (
                <div className="group relative z-0 mt-1 w-full">
                  {isDeleting ? (
                    <Loader height={20} width={20} type={'Wait'} />
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        removeBeneficary(index, beneficiary.beneficiaryId)
                      }
                    >
                      <Icon name={Icons.Delete} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
            {formik.values.beneficiaries[0].beneficiaryType && (
              <div className="group z-0 flex w-full items-center justify-end">
                <div
                  className="flex items-center gap-4"
                  onClick={addMoreBeneficary}
                  role="button"
                  tabIndex={-1}
                >
                  <Icon name={Icons.AddCircleOutline} />
                  <span className="mt-2 text-base font-medium text-[#353535]">
                    Add Another Beneficiary
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-5 flex w-full items-end justify-end">
            <Button
              variant="primary"
              size="custom"
              className="mt-5 w-[9rem] rounded p-2.5"
              label="Create Beneficiary"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </div>
      </form>
      {showSelectUser && (
        <EnhancedSelectMultipleBeneficiaryModal
          onClose={() => setShowSelectUser(false)}
          onAdd={(selectedUser: SpouseChildDto[]) =>
            onBeneficiarySelect(selectedUser)
          }
        />
      )}
    </>
  );
}
